import { TnAClockingSTO } from "../../models/tna";
import { TnAClockingRef } from "../../models/tna/tna-clockings/tna-clocking-ref";
import { TnADayDataRef } from "src/models/tna/tna-data/tna-day-data-ref";

export const diffInScheduleAndClocking = (
  date: Date,
  dayData: TnADayDataRef[] | undefined,
  forPrevDay: boolean
): number | null => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const dayOfWeek = date.getDay();

  const prevDayDate = new Date(year, month, day - 1);
  const prevDayYear = prevDayDate.getFullYear();
  const prevDayMonth = prevDayDate.getMonth();
  const prevDayExactDate = prevDayDate.getDate();

  const dayOfWeekForPrevDay = dayOfWeek - 1 < 0 ? 6 : dayOfWeek - 1;

  const currDay = dayData?.find(
    (currDayData) =>
      currDayData.tnADate?.year === year && currDayData.tnADate?.month === month + 1 && currDayData.tnADate?.day === day
  );

  const prevDay = dayData?.find(
    (currDayData) =>
      currDayData.tnADate?.year === prevDayYear &&
      currDayData.tnADate?.month === prevDayMonth + 1 &&
      currDayData.tnADate?.day === prevDayExactDate
  );

  if (!currDay) return null;

  const scheduleForThatDay = currDay.schedule?.intervals?.filter((sched) => sched.weekDay === dayOfWeek);

  const scheduleForPrevDay = currDay.schedule?.intervals?.filter((sched) => sched.weekDay === dayOfWeekForPrevDay);

  let diffInScheduleAndClockings: number | null = 0;

  if (forPrevDay) {
    if (!prevDay?.expectedPresence && !!prevDay) return null;
  } else {
    const intervalsForDayWithOnlyNextDay = scheduleForThatDay?.filter((interval) => interval?.isNextDay) ?? [];

    const expectedPresenceAccordingToIntervals = intervalsForDayWithOnlyNextDay.reduce((acc, curr) => {
      return (
        acc +
        (new Date(year, month, day, curr.endHour, curr.endMinute).getTime() -
          new Date(year, month, day, curr.startHour, curr.startMinute).getTime())
      );
    }, 0);

    if (currDay.expectedPresence - expectedPresenceAccordingToIntervals / 3600000 <= 0) return null;
  }

  if (forPrevDay) {
    const convertedDates =
      scheduleForPrevDay
        ?.filter((interval) => interval?.isNextDay)
        ?.map((sched) => [
          new Date(year, month, day, sched.startHour, sched.startMinute),
          new Date(year, month, day, sched.endHour, sched.endMinute),
        ]) ?? [];

    const isDateInIntervals = convertedDates.find(
      (interval) => interval[0].getTime() - date.getTime() <= 0 && interval[1].getTime() - date.getTime() >= 0
    );

    if (!isDateInIntervals) {
      const dates = convertedDates?.reduce((acc, curr) => [...acc, ...curr], []) ?? [];

      let closestDate = dates[0] ?? date;

      diffInScheduleAndClockings = Math.abs(date.getTime() - closestDate.getTime());

      for (const thisDate of dates) {
        const difference = Math.abs(date.getTime() - thisDate.getTime());
        if (difference < diffInScheduleAndClockings) {
          diffInScheduleAndClockings = difference;
          closestDate = thisDate;
        }
      }

      // convert to hours from milliseconds
      diffInScheduleAndClockings = diffInScheduleAndClockings / 3600000;
    }
  } else {
    const convertedDates =
      scheduleForThatDay
        ?.filter((interval) => !interval?.isNextDay)
        ?.map((sched) => [
          new Date(year, month, day, sched.startHour, sched.startMinute),
          new Date(year, month, day, sched.endHour, sched.endMinute),
        ]) ?? [];

    const isDateInIntervals = convertedDates.find(
      (interval) => interval[0].getTime() - date.getTime() <= 0 && interval[1].getTime() - date.getTime() >= 0
    );

    if (!isDateInIntervals) {
      const dates = convertedDates?.reduce((acc, curr) => [...acc, ...curr], []) ?? [];

      let closestDate = dates[0] ?? date;

      diffInScheduleAndClockings = Math.abs(date.getTime() - closestDate.getTime());

      for (const thisDate of dates) {
        const difference = Math.abs(date.getTime() - thisDate.getTime());
        if (difference < diffInScheduleAndClockings) {
          diffInScheduleAndClockings = difference;
          closestDate = thisDate;
        }
      }

      // convert to hours from milliseconds
      diffInScheduleAndClockings = diffInScheduleAndClockings / 3600000;
    }
  }

  return diffInScheduleAndClockings;
};

export const diffInScheduleAndClockingForWeek = (
  clockings: (Partial<TnAClockingSTO> | TnAClockingRef)[],
  dayData: TnADayDataRef[] | undefined,
  changedPrevDayArray: string[]
): DiffInScheduleAndClockingForWeekType[] => {
  const result: DiffInScheduleAndClockingForWeekType[] = [];

  if (!!clockings.length) {
    for (let i = 0; i < clockings.length; i++) {
      const currClocking = clockings[i];

      const year = currClocking.tnADetail?.year ?? new Date().getFullYear();
      const month = currClocking.tnADetail?.month ? currClocking.tnADetail?.month - 1 : new Date().getMonth();
      const day = currClocking.tnADetail?.day ?? new Date().getDate();
      const hours = currClocking.tnADetail?.hour ?? new Date().getHours();
      const minutes = currClocking.tnADetail?.minute ?? new Date().getMinutes();
      const seconds = currClocking.tnADetail?.second ?? new Date().getSeconds();

      const currDate = new Date(year, month, day, hours, minutes, seconds);

      const prevDayPropChanged = currClocking?.id ? changedPrevDayArray.includes(currClocking.id) : false;

      const differenceInClockingsAndSchedule = diffInScheduleAndClocking(
        currDate,
        dayData,
        prevDayPropChanged ? !currClocking?.forPrevDay : !!currClocking?.forPrevDay
      );

      if (differenceInClockingsAndSchedule === null || differenceInClockingsAndSchedule > 1) {
        result.push({
          date: currDate,
          clockingTypeId: currClocking?.clockingType?.id as string,
          hoursBeforeClosestInterval: differenceInClockingsAndSchedule,
          forPrevDay: prevDayPropChanged ? !currClocking?.forPrevDay : !!currClocking?.forPrevDay,
        });
      }
    }
  }

  return result;
};

export interface DiffInScheduleAndClockingForWeekType {
  date: Date;
  clockingTypeId: string;
  hoursBeforeClosestInterval: number | null;
  forPrevDay: boolean;
}
